import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppFooter from "@/components/layout/AppFooter";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const LOGO_URL = "/smartdienstplan-logo.svg";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { forgotPassword, resetPassword } = useAuth();
  const token = (searchParams.get("token") || "").trim();
  const hasToken = useMemo(() => token.length > 0, [token]);

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }
    setLoading(true);
    try {
      const response = await forgotPassword(email);
      toast.success(response?.message || "Wenn ein Konto existiert, wurde ein Link gesendet.");
      setEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anfrage fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Das neue Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword(token, newPassword);
      toast.success(response?.message || "Passwort wurde zurückgesetzt.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="forgot-password-page">
      <div className="auth-container flex-1">
        <div className="auth-image-side bg-[#212529]">
          <div className="absolute inset-0 bg-[#212529] flex items-center justify-center">
            <div className="text-center p-8">
              <img src={LOGO_URL} alt="smartdienstplan" className="w-32 h-32 mx-auto mb-8" />
              <h2 className="font-heading text-3xl font-bold text-white mb-4">Passwort vergessen?</h2>
              <p className="text-gray-400 text-lg max-w-md">
                {hasToken
                  ? "Setze jetzt ein neues Passwort für dein Konto."
                  : "Wir senden dir einen Link zum Zurücksetzen deines Passworts."}
              </p>
            </div>
          </div>
        </div>

        <div className="auth-form-side bg-[#f8fafc]">
          <div className="w-full max-w-md">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <div className="w-8 h-8 bg-[#212529] rounded-lg flex items-center justify-center p-1">
                <img src={LOGO_URL} alt="smartdienstplan" className="w-full h-full" />
              </div>
              <span className="font-heading font-semibold text-xl text-[#212529]">smartdienstplan</span>
            </Link>

            <Card className="border-gray-200 bg-white shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl text-[#212529]">
                  {hasToken ? "Neues Passwort setzen" : "Passwort zurücksetzen"}
                </CardTitle>
                <CardDescription className="text-gray-500">
                  {hasToken
                    ? "Gib dein neues Passwort ein."
                    : "Gib deine E-Mail-Adresse ein. Du erhältst dann einen Link."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hasToken ? (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-password" className="text-[#212529]">Neues Passwort</Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="h-11 pr-10 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password" className="text-[#212529]">Passwort wiederholen</Label>
                      <Input
                        id="confirm-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      />
                    </div>

                    <Button type="submit" className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Speichern...
                        </>
                      ) : (
                        "Passwort speichern"
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleRequestReset} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#212529]">E-Mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="deine@email.de"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                      />
                    </div>
                    <Button type="submit" className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Senden...
                        </>
                      ) : (
                        "Link senden"
                      )}
                    </Button>
                  </form>
                )}

                <div className="mt-6 text-center text-sm text-gray-500">
                  <Link to="/login" className="text-[#17c671] hover:text-[#14a85d] font-medium">
                    Zurück zur Anmeldung
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default ForgotPasswordPage;
